import { GeneralCatalogItem, ServerResponse } from 'data-layer-cr/dist/models';
import { setRequestStatus } from 'data-layer-cr/dist/network';
import { AnyAction } from 'redux';
import { BirthCountry } from 'src/models/responseTypes/BirthPlaceResponse';
import { CivilStatusCatalog } from 'src/models/responseTypes/CivilStatusResponse';
import processRequest from 'src/services/processRequest';
import { AsyncAppThunk, AsyncDispatch } from '../../ThunkTypes';

import {
  updateBeneficiaryBirthPlace,
  updateBeneficiaryBirthPlaceMap,
  updateBeneficiaryCities,
  updateBeneficiaryCountries,
  updateBeneficiaryCountriesMap,
  updateBeneficiaryCountryStates,
  updateBeneficiaryNationality,
  updateBeneficiaryNationalityMap,
  updateBeneficiaryOccupations,
  updateBeneficiaryOccupationsMap,
  updateBeneficiaryTownships,
  updateBirthPlaces,
  updateCities,
  updateCivilStatus,
  updateCountries,
  updateCountriesMap,
  updateCountryStates,
  updateEconomicActivities,
  updateEconomicActivitiesMap,
  updateOccupations,
  updateOccupationsMap,
  updateRestrictedBirthPlaces,
  updateTownships,
} from '../slice';

import {
  BENEFICIARY_BIRTH_PLACE_REQUEST,
  BENEFICIARY_CITIES_REQUEST,
  BENEFICIARY_COUNTRIES_REQUEST,
  BENEFICIARY_NATIONALITY_REQUEST,
  BENEFICIARY_OCCUPATIONS_REQUEST,
  BENEFICIARY_STATES_REQUEST,
  BENEFICIARY_TOWNSHIPS_REQUEST,
  BIRTH_PLACE_REQUEST,
  CITIES_REQUEST,
  CIVIL_STATUS_REQUEST,
  COUNTRIES_REQUEST,
  ECONOMIC_ACTIVITIES_REQUEST,
  OCCUPATIONS_REQUEST,
  RESTRICTED_BIRTH_PLACE_REQUEST,
  STATES_REQUEST,
  TOWNSHIPS_REQUEST,
} from './legalEntityRequestIdentifiers';

/**
 * Requests the list of countries that can be assigned to a legal entity
 */
export const getCountries =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(COUNTRIES_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: 'catalog/location/countrycode',
        },
      );
      dispatch(updateCountries(result.data));

      // build {code:name} map of countries
      const countriesMap: { [key: string]: string } = {};
      result.data.forEach((country) => {
        countriesMap[country.code] = country.name;
      });

      dispatch(updateCountriesMap(countriesMap));

      return dispatch(setRequestStatus(COUNTRIES_REQUEST, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(COUNTRIES_REQUEST, 'failure'));
    }
  };

export const getBeneficiaryCountries =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(BENEFICIARY_COUNTRIES_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: 'catalog/location/countrycode',
        },
      );
      dispatch(updateBeneficiaryCountries(result.data));

      // build {code:name} map of countries
      const countriesMap: { [key: string]: string } = {};
      result.data.forEach((country) => {
        countriesMap[country.code] = country.name;
      });

      dispatch(updateBeneficiaryCountriesMap(countriesMap));

      return dispatch(
        setRequestStatus(BENEFICIARY_COUNTRIES_REQUEST, 'success'),
      );
    } catch (e) {
      return dispatch(
        setRequestStatus(BENEFICIARY_COUNTRIES_REQUEST, 'failure'),
      );
    }
  };

export const getBeneficiaryNationality =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(BENEFICIARY_NATIONALITY_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: 'catalog/location/countrycode',
        },
      );
      dispatch(updateBeneficiaryNationality(result.data));

      // build {code:name} map of countries
      const countriesMap: { [key: string]: string } = {};
      result.data.forEach((country) => {
        countriesMap[country.code] = country.name;
      });

      dispatch(updateBeneficiaryNationalityMap(countriesMap));

      return dispatch(
        setRequestStatus(BENEFICIARY_NATIONALITY_REQUEST, 'success'),
      );
    } catch (e) {
      return dispatch(
        setRequestStatus(BENEFICIARY_NATIONALITY_REQUEST, 'failure'),
      );
    }
  };

export const getBeneficiaryBirthPlace =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(BENEFICIARY_BIRTH_PLACE_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: 'catalog/location/countrycode',
        },
      );
      dispatch(updateBeneficiaryBirthPlace(result.data));

      // build {code:name} map of countries
      const countriesMap: { [key: string]: string } = {};
      result.data.forEach((country) => {
        countriesMap[country.code] = country.name;
      });

      dispatch(updateBeneficiaryBirthPlaceMap(countriesMap));

      return dispatch(
        setRequestStatus(BENEFICIARY_BIRTH_PLACE_REQUEST, 'success'),
      );
    } catch (e) {
      return dispatch(
        setRequestStatus(BENEFICIARY_BIRTH_PLACE_REQUEST, 'failure'),
      );
    }
  };

/**
 * Requests a list of economic activities that can be assigned to a legal entity
 */
export const getOccupations =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(OCCUPATIONS_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: 'occupations',
        },
      );
      dispatch(updateOccupations(result.data));

      // build {code:name} map of occupations
      const occupationsMap: { [key: string]: string } = {};
      result.data.forEach((occupation) => {
        occupationsMap[occupation.code] = occupation.name;
      });

      dispatch(updateOccupationsMap(occupationsMap));

      return dispatch(setRequestStatus(OCCUPATIONS_REQUEST, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(OCCUPATIONS_REQUEST, 'failure'));
    }
  };

export const getBeneficiaryOccupations =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(BENEFICIARY_OCCUPATIONS_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: 'occupations',
        },
      );
      dispatch(updateBeneficiaryOccupations(result.data));

      // build {code:name} map of occupations
      const occupationsMap: { [key: string]: string } = {};
      result.data.forEach((occupation) => {
        occupationsMap[occupation.code] = occupation.name;
      });

      dispatch(updateBeneficiaryOccupationsMap(occupationsMap));

      return dispatch(
        setRequestStatus(BENEFICIARY_OCCUPATIONS_REQUEST, 'success'),
      );
    } catch (e) {
      return dispatch(
        setRequestStatus(BENEFICIARY_OCCUPATIONS_REQUEST, 'failure'),
      );
    }
  };
export const getEconomicAcitivies =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(ECONOMIC_ACTIVITIES_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: 'quotation/economicActivities',
        },
      );
      dispatch(updateEconomicActivities(result.data));

      const economicActivitiesMap: { [key: string]: string } = {};
      result.data.forEach((economicActivity) => {
        economicActivitiesMap[economicActivity.code] = economicActivity.name;
      });

      dispatch(updateEconomicActivitiesMap(economicActivitiesMap));

      return dispatch(setRequestStatus(ECONOMIC_ACTIVITIES_REQUEST, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(ECONOMIC_ACTIVITIES_REQUEST, 'failure'));
    }
  };

/**
 * Requests the list of states that can be assigned to an address
 */
export const getCountryStates =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(STATES_REQUEST, 'requesting'));
    try {
      const result = await processRequest<GeneralCatalogItem[]>({
        method: 'GET',
        url: 'catalog/locations/states',
      });
      dispatch(updateCountryStates(result));
      return dispatch(setRequestStatus(STATES_REQUEST, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(STATES_REQUEST, 'failure'));
    }
  };

export const getBeneficiaryCountryStates =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(BENEFICIARY_STATES_REQUEST, 'requesting'));
    try {
      const result = await processRequest<GeneralCatalogItem[]>({
        method: 'GET',
        url: 'catalog/locations/states',
      });
      dispatch(updateBeneficiaryCountryStates(result));
      return dispatch(setRequestStatus(BENEFICIARY_STATES_REQUEST, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(BENEFICIARY_STATES_REQUEST, 'failure'));
    }
  };

/**
 * Requests the list of cities that can be assigned to an address
 */
export const getCities =
  (stateCode: string | number): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(CITIES_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: `catalog/location/cities?stateId=${stateCode}`,
        },
      );
      dispatch(updateCities(result.data));
      return dispatch(setRequestStatus(CITIES_REQUEST, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(CITIES_REQUEST, 'failure'));
    }
  };

export const getBeneficiaryCities =
  (stateCode: string | number): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(BENEFICIARY_CITIES_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: `catalog/location/cities?stateId=${stateCode}`,
        },
      );
      dispatch(updateBeneficiaryCities(result.data));
      return dispatch(setRequestStatus(BENEFICIARY_CITIES_REQUEST, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(BENEFICIARY_CITIES_REQUEST, 'failure'));
    }
  };

/**
 * Requests the list of cities Townships can be assigned to an address
 */
export const getTownships =
  (stateCode: string | number): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(TOWNSHIPS_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: `catalog/location/townships?cityid=${stateCode}`,
        },
      );
      dispatch(updateTownships(result.data));
      return dispatch(setRequestStatus(TOWNSHIPS_REQUEST, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(TOWNSHIPS_REQUEST, 'failure'));
    }
  };

export const getBeneficiaryTownships =
  (stateCode: string | number): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(BENEFICIARY_TOWNSHIPS_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: `catalog/location/townships?cityid=${stateCode}`,
        },
      );
      dispatch(updateBeneficiaryTownships(result.data));
      return dispatch(
        setRequestStatus(BENEFICIARY_TOWNSHIPS_REQUEST, 'success'),
      );
    } catch (e) {
      return dispatch(
        setRequestStatus(BENEFICIARY_TOWNSHIPS_REQUEST, 'failure'),
      );
    }
  };

/**
 * Requests the list of civil status that can be assigned to natural person
 */
export const getCivilStatus =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(CIVIL_STATUS_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<CivilStatusCatalog[]>>(
        {
          method: 'GET',
          url: 'catalog/policy/civilstatus',
        },
      );
      dispatch(
        updateCivilStatus(
          result.data.map(({ id, code, description }) => ({
            id,
            code,
            name: description,
          })),
        ),
      );
      return dispatch(setRequestStatus(CIVIL_STATUS_REQUEST, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(CIVIL_STATUS_REQUEST, 'failure'));
    }
  };

/**
 * Requests the list of civil status that can be assigned to natural person
 */

export const getBirthPlaces =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(BIRTH_PLACE_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<BirthCountry[]>>({
        method: 'GET',
        url: 'catalog/location/countries',
      });

      const birthPlaces: GeneralCatalogItem[] = [];
      result.data.forEach((country) => {
        if (country.states) {
          country.states.forEach((state) => {
            birthPlaces.push({
              code: `${country.code}-${state.code}`,
              id: country.id * 100_000 + state.id,
              name: `${country.name}, ${state.name}`,
            });
          });
        } else {
          birthPlaces.push({
            id: country.id,
            code: country.code,
            name: country.name,
          });
        }
      });

      dispatch(updateBirthPlaces(birthPlaces));
      return dispatch(setRequestStatus(BIRTH_PLACE_REQUEST, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(BIRTH_PLACE_REQUEST, 'failure'));
    }
  };

export const getRestrictedBirthPlaces =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(RESTRICTED_BIRTH_PLACE_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<GeneralCatalogItem[]>>(
        {
          method: 'GET',
          url: '/quotation/restrictedCountries',
        },
      );

      dispatch(updateRestrictedBirthPlaces(result.data));
      return dispatch(
        setRequestStatus(RESTRICTED_BIRTH_PLACE_REQUEST, 'success'),
      );
    } catch {
      return dispatch(
        setRequestStatus(RESTRICTED_BIRTH_PLACE_REQUEST, 'failure'),
      );
    }
  };
